// REQ-CD-P1-CON-05 / review issue 6 — the Watcher returns 401/403 both for a
// genuinely expired/invalid Access JWT (an Access challenge) and for an
// application-level refusal such as an upward mode transition refused by a
// service token (not a challenge — render verbatim). The two are only
// distinguishable by this machine code in the response body; anything else
// on a 401/403, including no body at all, is classified as accessRequired.
// Coordinate with workers/watcher on this string — it must match what
// workers/watcher/src/routes/api.ts sends.
const APPLICATION_REFUSAL_CODES = new Set(["upward_transition_requires_operator"]);

// SEC-CD-P1-21 — `fetch(` appears nowhere under src/ except this file. Every
// GET read calls `request()` directly; `mutate()` (api/mutate.ts) and the
// Phase-0 `fetchHealth()` (api/health.ts) both route their calls through it
// too, so this is the console's single network call site.
export class ApiError extends Error {
  readonly status: number;
  /** A 503 on an /api/* route means ACCESS_AUD is unset (REQ-CD-P1-DEF-xx) — render "API not configured". */
  readonly notConfigured: boolean;
  /** A 404 — a real "not found", distinct from a generic error (e.g. "no verdict yet"). */
  readonly notFound: boolean;
  /**
   * D-CD-60 / REQ-CD-P1-CON-10 — true when this rejection means the operator
   * needs to sign in to this origin. request() never acts on it (SEC-CD-P1-27);
   * it is the caller's job to report it to useAccessStore.
   */
  readonly accessRequired: boolean;

  constructor(
    message: string,
    opts: { status?: number; notConfigured?: boolean; notFound?: boolean; accessRequired?: boolean } = {},
  ) {
    super(message);
    this.status = opts.status ?? 0;
    this.notConfigured = opts.notConfigured ?? false;
    this.notFound = opts.notFound ?? false;
    this.accessRequired = opts.accessRequired ?? false;
  }
}

export interface RequestOptions {
  method?: "GET" | "POST";
  body?: unknown;
  /**
   * "not_configured" (default) — a 503 means the /api/* route's ACCESS_AUD
   * is empty and is surfaced as ApiError.notConfigured, with no body read.
   * "body" — a 503 carries a legitimate response body (the Phase-0 /health
   * contract, where 503 means `status: "degraded"`, a different condition
   * that happens to share the status code on a different, unauthenticated
   * route).
   */
  on503?: "not_configured" | "body";
  /**
   * REQ-CD-P1-CON-05: the Watcher's /api/control/mode returns 401/403 for an
   * upward transition refused by a service token — an application-level
   * refusal to render verbatim, not an Access challenge. mutate() sets this
   * to allow that case to skip accessRequired classification, but only when
   * the response body actually carries a recognized APPLICATION_REFUSAL_CODES
   * entry; a bare 401/403 with no matching code is still classified
   * accessRequired, since that's indistinguishable from a genuinely
   * expired/invalid Access session.
   */
  recognizeApplicationRefusal?: boolean;
}

export async function request<T>(baseUrl: string, path: string, opts: RequestOptions = {}): Promise<T | null> {
  let res: Response;
  try {
    res = await fetch(`${baseUrl.replace(/\/+$/, "")}${path}`, {
      method: opts.method ?? "GET",
      credentials: "include",
      headers: opts.body !== undefined ? { "Content-Type": "application/json" } : undefined,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    });
  } catch (err) {
    // No response to inspect (offline, DNS, CORS-blocked opaque redirect).
    // D-CD-60: classified accessRequired only for a GET to an Access-fronted
    // path (D-CD-49's predicate) — that is the shape a cookie-less Access
    // challenge takes. Everything else (non-GET, non-/api/*, unparseable
    // base) is a plain network error. baseUrl can legitimately be ""
    // (§14 item 2 — no dev-default fallback), in which case new URL(...)
    // throws TypeError: Invalid URL; that must never replace the ApiError
    // this branch is contractually obligated to throw, so the classification
    // is isolated in its own try/catch and simply falls through to `false`.
    let accessRequired = false;
    try {
      const isAccessFronted = new URL(`${baseUrl.replace(/\/+$/, "")}${path}`).pathname.startsWith("/api/");
      accessRequired = (opts.method ?? "GET") === "GET" && isAccessFronted;
    } catch {
      /* baseUrl not a parseable absolute URL — falls through to accessRequired: false */
    }
    throw new ApiError(`Network error reaching ${baseUrl}: ${(err as Error).message}`, { accessRequired });
  }

  if (res.status === 401 || res.status === 403) {
    if (opts.recognizeApplicationRefusal) {
      const body = (await res.json().catch(() => null)) as { error?: unknown } | null;
      const code = body && typeof body.error === "string" ? body.error : undefined;
      if (code && APPLICATION_REFUSAL_CODES.has(code)) {
        throw new ApiError(code, { status: res.status });
      }
    }
    throw new ApiError(`Unauthenticated (HTTP ${res.status})`, { status: res.status, accessRequired: true });
  }

  if (res.status === 503 && (opts.on503 ?? "not_configured") === "not_configured") {
    throw new ApiError("API not configured", { status: 503, notConfigured: true });
  }

  if (res.status === 404) {
    throw new ApiError("Not found", { status: 404, notFound: true });
  }

  if (!res.ok && res.status !== 503) {
    const body = (await res.json().catch(() => null)) as { error?: unknown } | null;
    const message = body && typeof body.error === "string" ? body.error : `HTTP ${res.status}`;
    throw new ApiError(message, { status: res.status });
  }

  return (await res.json().catch(() => null)) as T | null;
}
